/**
 * ChartCard — consistent wrapper for every chart/table on the analytics
 * tabs. Surface for: title, optional subtitle, top-right action slot
 * (CSV export, toggle), loading/error/empty states.
 *
 * Adopting this everywhere keeps the visual rhythm tight + makes adding
 * a new chart a 10-line task (just provide the inner viz).
 */

import { ReactNode } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/cn';

interface Props {
  title: string;
  subtitle?: string;
  /** Top-right slot — CSV export buttons, toggles, etc. */
  actions?: ReactNode;
  /** Inner content (chart / table). Hidden when loading/error/empty. */
  children: ReactNode;
  isLoading?: boolean;
  isError?: boolean;
  isEmpty?: boolean;
  emptyLabel?: string;
  /** Optional extra Tailwind classes. */
  className?: string;
}

export default function ChartCard({
  title, subtitle, actions, children,
  isLoading, isError, isEmpty,
  emptyLabel = 'No data.',
  className,
}: Props) {
  return (
    <div className={cn('rounded border border-slate-800 bg-slate-900/30', className)}>
      <header className="flex items-start justify-between gap-2 px-3 py-2 border-b border-slate-800">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wide text-slate-400">{title}</div>
          {subtitle && <div className="text-[11px] text-slate-500 mt-0.5">{subtitle}</div>}
        </div>
        {actions && <div className="shrink-0">{actions}</div>}
      </header>
      <div className="p-3 text-sm">
        {isLoading ? (
          <div className="flex items-center gap-2 text-xs text-slate-500 py-4">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading…
          </div>
        ) : isError ? (
          <div className="flex items-center gap-2 text-xs text-rose-400 py-4">
            <AlertCircle className="w-3 h-3" /> Failed to load.
          </div>
        ) : isEmpty ? (
          <div className="text-xs text-slate-500 py-4">{emptyLabel}</div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}
